<template>
  <!-- Below md the document scrolls (the bottom nav is fixed); from md up the
       page is pinned to the viewport and only the dashboard body scrolls, so
       there is never a second scrollbar behind it.
       The breakpoint is md, not sm, because that is where the sidebar, the
       mobile navbar and the bottom nav all switch. At sm the page would be
       100vh tall while the sticky mobile navbar was still on screen, leaving
       the document a little taller than the viewport — and scrolling past the
       end of the inner container would chain to it and drag the whole page,
       sidebar included, upwards. `overscroll-contain` on the scrollers stops
       that chaining for good measure. -->
  <div class="flex flex-col grow bg-gray-50 dark:bg-[#0d1117] md:h-screen md:overflow-hidden">
    <!-- Header ---------------------------------------------------------- -->
    <div class="flex-shrink-0 sticky top-0 z-10 bg-white dark:bg-[#1C2128] border-b border-gray-200 dark:border-gray-700">
      <div class="flex items-center justify-between gap-3 px-4 sm:px-6 pt-3 sm:pt-3.5">
        <div class="min-w-0">
          <h1 class="text-[22px] sm:text-xl font-bold text-gray-900 dark:text-white tracking-tight leading-none">{{ section.label }}</h1>
          <p v-if="subtitle" class="text-[11px] text-gray-400 dark:text-gray-500 mt-1 truncate">{{ subtitle }}</p>
        </div>

        <div v-if="section.usesSharedRange" class="flex items-center gap-2 flex-shrink-0">
          <button type="button" @click="refresh" :disabled="loading" title="Refresh"
            class="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors disabled:opacity-40">
            <svg class="w-4 h-4" :class="loading ? 'animate-spin' : ''" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
            </svg>
          </button>

          <!-- Segmented on desktop, a select on mobile: seven pills do not fit
               on a phone without becoming unhittable. -->
          <div class="hidden md:flex items-center gap-0.5 bg-gray-100 dark:bg-gray-800 rounded-lg p-1">
            <button v-for="option in RANGES" :key="option.value" type="button" @click="store.setRange(option.value)"
              class="px-2.5 py-1 text-xs font-semibold rounded-md transition-all"
              :class="store.range === option.value
                ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'">
              {{ option.label }}
            </button>
          </div>
          <select :value="store.range" @change="store.setRange($event.target.value)"
            class="md:hidden text-xs font-semibold text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 border-0 rounded-lg px-2.5 py-1.5 focus:ring-2 focus:ring-blue-500 outline-none">
            <option v-for="option in RANGES" :key="option.value" :value="option.value">{{ option.long }}</option>
          </select>
        </div>

        <!-- Same slot, same shape, different store. -->
        <StatsRangeControls v-else-if="activeTab === 'tasks'" />
      </div>

      <!-- Second header row: every section has its own dimension filters and an
           export, in one place, rather than a bar of its own below the header. -->
      <StatsFilters v-if="activeTab === 'tasks'" class="px-4 sm:px-6 mt-3" />
      <AnalyticsFilters v-else :tab="activeTab" class="px-4 sm:px-6 mt-3" />

      <!-- Below md the sidebar is hidden, so the page carries its own section
           switcher. On desktop the sidebar sub-nav is the only one. -->
      <div class="md:hidden flex gap-5 px-4 sm:px-6 mt-2.5">
        <button v-for="s in SECTIONS" :key="s.key" type="button" @click="switchSection(s.key)"
          class="pb-2.5 pt-0.5 text-sm font-semibold border-b-2 transition-colors"
          :class="activeTab === s.key
            ? 'border-blue-600 text-blue-600 dark:text-blue-400'
            : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'">
          {{ s.label }}
        </button>
      </div>
      <div class="hidden md:block pb-3"></div>
    </div>

    <!-- Error ----------------------------------------------------------- -->
    <div v-if="error" class="flex flex-col items-center justify-center py-32 text-center px-4 md:flex-1 md:min-h-0 md:overflow-y-auto overscroll-contain">
      <div class="w-16 h-16 rounded-full bg-red-50 dark:bg-red-900/20 flex items-center justify-center mb-4">
        <svg class="w-7 h-7 text-red-400" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
        </svg>
      </div>
      <h3 class="text-base font-semibold text-gray-900 dark:text-white mb-1">Something went wrong</h3>
      <p class="text-sm text-gray-500 dark:text-gray-400 mb-5">{{ error }}</p>
      <button type="button" @click="refresh"
        class="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors">
        Try again
      </button>
    </div>

    <!-- Tasks ------------------------------------------------------------
         Brings its own store, filter bar, scroll container and methodology
         panel, so it sits beside the shared dashboard column rather than
         inside it. -->
    <TasksTab v-else-if="activeTab === 'tasks'" />

    <!-- Dashboard ------------------------------------------------------- -->
    <div v-else class="px-4 sm:px-6 py-4 sm:py-5 space-y-4 pb-24 md:pb-8 md:flex-1 md:min-h-0 md:overflow-y-auto overscroll-contain">
      <BookingsTab v-if="activeTab === 'bookings'" :stats="store.bookingsStats" :loading="loading" />
      <TrafficTab  v-else :stats="store.trafficStats" :loading="loading" />

      <MethodologyPanel :tab="activeTab" :zone="rangeMeta?.zone || ''" />
    </div>
  </div>
</template>

<script setup>
import { computed, watch, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useGlobalStore } from 'stores/global.js'
import { useAnalyticsStore, RANGES } from 'stores/analytics.js'
import { useTaskStatsStore } from 'stores/taskStats.js'
import { taskRangeLabel } from './tasks/statsRanges.js'
import BookingsTab from './bookings/BookingsTab.vue'
import TasksTab from './tasks/TasksTab.vue'
import StatsRangeControls from './tasks/StatsRangeControls.vue'
import StatsFilters from './tasks/StatsFilters.vue'
import AnalyticsFilters from './shared/AnalyticsFilters.vue'
import { useAnalyticsUrlState } from 'composables/useAnalyticsUrlState.js'
import TrafficTab from './traffic/TrafficTab.vue'
import MethodologyPanel from './shared/MethodologyPanel.vue'

// `usesSharedRange` marks the sections backed by useAnalyticsStore and the
// range picker in this header. Tasks is not one of them: it has its own store
// (useTaskStatsStore) and its own filter bar, so this header shows only its
// title and the section switcher.
const SECTIONS = [
  { key: 'bookings', label: 'Bookings', path: '/analytics/bookings', usesSharedRange: true },
  { key: 'tasks',    label: 'Tasks',    path: '/analytics/tasks',    usesSharedRange: false },
  { key: 'traffic',  label: 'Traffic',  path: '/analytics/traffic',  usesSharedRange: true },
]

const route = useRoute()
const router = useRouter()
const store = useAnalyticsStore()

// The section is derived from the URL rather than held in local state, so a
// refresh, a back button press and a shared link all land on the same view.
const section = computed(() =>
  SECTIONS.find(s => route.path.startsWith(s.path)) ?? SECTIONS[0]
)
const activeTab = computed(() => section.value.key)

const loading = computed(() => (section.value.usesSharedRange ? store[activeTab.value].loading : false))
const error = computed(() => (section.value.usesSharedRange ? store[activeTab.value].error : null))
const rangeMeta = computed(() => {
  if (!section.value.usesSharedRange) return null
  return (activeTab.value === 'traffic' ? store.trafficStats : store.bookingsStats)?.range
})

// Bookings and Traffic get their window description from the server payload.
// Tasks has no server-side label, so it is derived from the filters.
const taskStatsStore = useTaskStatsStore()
const subtitle = computed(() =>
  activeTab.value === 'tasks'
    ? taskRangeLabel(taskStatsStore.filters)
    : rangeMeta.value?.label
)

function switchSection(key) {
  const target = SECTIONS.find(s => s.key === key)
  if (target) router.push(target.path)
}

// ── URL as shareable state ────────────────────────────────────────────────
// The Tasks section owns its own query string inside TasksTab.vue; this owns
// the other two. URL beats localStorage, so opening a link someone sent shows
// you their dashboard rather than yours. Applied in setup, before the first
// fetch, so the request already carries the right filters.
const { readState, writeState } = useAnalyticsUrlState()

if (section.value.usesSharedRange) {
  const fromUrl = readState(activeTab.value)
  if (fromUrl.range) store.hydrateRange(fromUrl.range)
  store.hydrateFilters(activeTab.value, fromUrl.filters)
}

function syncUrl() {
  if (!section.value.usesSharedRange) return
  writeState(activeTab.value, store.range, store.filters[activeTab.value])
}

function load() {
  // Only the visible section is fetched. The payloads are large and most
  // visits never open a second one; the store keeps whichever has been
  // loaded, so switching back is instant.
  if (!section.value.usesSharedRange) return
  syncUrl()
  store.fetchIfStale(activeTab.value)
}

async function refresh() {
  if (!section.value.usesSharedRange) return
  await store.refresh(activeTab.value)
}

watch([activeTab, () => store.range], load)

// Filters are changed through the store, which refetches itself; this only has
// to keep the URL in step. Replace, never push, so Back does not become an undo
// trail of every checkbox touched.
watch(() => store.filters[activeTab.value], syncUrl, { deep: true })

onMounted(() => {
  useGlobalStore().fetchCurrentUser()
  // Reflect whatever was restored from localStorage, so the URL is shareable
  // immediately rather than only after the first interaction.
  syncUrl()
  load()
})
</script>
